/**
 * TemplatesDrawer.tsx
 * Drawer pour la sélection de templates
 * Extrait de ModernSidebar.tsx
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useAppStore } from '../../../../services/StateService';
import { TemplateService, type TemplatePreset } from '../../../../services/TemplateService';
import { logger } from '../../../../utils/ProfessionalLogger';
import { useAppCoordinatorOptional } from '../../../../hooks/useAppCoordinator';

interface TemplatesDrawerProps {
  onClose: () => void;
}

const CATEGORY_FILTERS: { id: string; label: string }[] = [
  { id: 'all', label: 'Tous' },
  { id: 'wedding', label: 'Mariage' },
  { id: 'birthday', label: 'Anniversaire' },
  { id: 'event', label: 'Soirée' },
  { id: 'corporate', label: 'Entreprise' },
  { id: 'holiday', label: 'Fêtes' },
];

export const TemplatesDrawer: React.FC<TemplatesDrawerProps> = ({ onClose }) => {
  const setShowTemplateGallery = useAppStore(state => state.setShowTemplateGallery);
  const [templates, setTemplates] = useState<TemplatePreset[]>([]);
  const [loading, setLoading] = useState(true);
  const [service] = useState(() => new TemplateService());
  // P3 redesign : search + filtre catégorie
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<string>('all');

  // Lazy loading et pagination pour les templates
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  // Charger les templates depuis TemplateService
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        setLoading(true);

        // Attendre que les templates soient chargés
        const allPresets = await service.getPresets();

        if (!allPresets || allPresets.length === 0) {
          // Fallback vers les templates locaux si aucun template n'est disponible
          const localTemplates = service.getPresetsSync();
          if (localTemplates && localTemplates.length > 0) {
            setTemplates(localTemplates.slice(0, 6));
            setHasMore(localTemplates.length > 6);
            return;
          }
          // Si toujours rien, laisser vide
          setTemplates([]);
          setHasMore(false);
          return;
        }

        // Grouper par catégorie pour afficher les plus populaires
        const categories = ['wedding', 'birthday', 'corporate', 'event'];
        const featuredTemplates: TemplatePreset[] = [];

        categories.forEach(category => {
          const categoryTemplates = allPresets.filter(p => p.category === category);
          if (categoryTemplates.length > 0) {
            featuredTemplates.push(categoryTemplates[0]);
          }
        });

        // Si pas assez de templates par catégorie, prendre les premiers disponibles
        if (featuredTemplates.length < 3) {
          const additionalTemplates = allPresets
            .filter(t => !featuredTemplates.some(ft => ft.id === t.id))
            .slice(0, 3 - featuredTemplates.length);
          featuredTemplates.push(...additionalTemplates);
        }

        setTemplates(featuredTemplates.slice(0, 6));

        // Vérifier s'il y a plus de templates
        try {
          const paginatedResult = await service.getPresetsPaginated(1);
          setHasMore(paginatedResult.hasNext || allPresets.length > 6);
        } catch (pagError) {
          // Si la pagination échoue, utiliser la longueur simple
          logger.warn('Erreur lors de la pagination, utilisation de la longueur simple:', pagError);
          setHasMore(allPresets.length > 6);
        }
      } catch (error) {
        logger.error('Erreur lors du chargement des templates:', error);
        try {
          // Fallback vers les templates locaux
          const localTemplates = service.getPresetsSync();
          if (localTemplates && localTemplates.length > 0) {
            setTemplates(localTemplates.slice(0, 6));
            setHasMore(localTemplates.length > 6);
          } else {
            setTemplates([]);
            setHasMore(false);
          }
        } catch (fallbackError) {
          logger.error('Erreur lors du chargement des templates locaux:', fallbackError);
          setTemplates([]);
          setHasMore(false);
        }
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, [service]);

  // Fonction pour charger plus de templates
  const loadMoreTemplates = useCallback(async () => {
    if (!hasMore || loading) return;

    try {
      setLoading(true);
      const nextPage = page + 1;
      const paginatedResult = await service.getPresetsPaginated(nextPage);
      const newTemplates = paginatedResult.items;

      setTemplates(prev => [...prev, ...newTemplates.slice(0, 6)]);
      setPage(nextPage);
      setHasMore(paginatedResult.hasNext);
    } catch (error) {
      logger.error('Erreur lors du chargement de plus de templates:', error);
    } finally {
      setLoading(false);
    }
  }, [service, page, hasMore, loading]);

  const handleOpenGallery = useCallback(() => {
    setShowTemplateGallery(true);
    onClose();
  }, [setShowTemplateGallery, onClose]);

  const appCoordinator = useAppCoordinatorOptional();

  const handleTemplateClick = useCallback(
    (template: TemplatePreset) => {
      if (appCoordinator) {
        try {
          // P3 : méthode pas exposée par le type, présente à l'instance.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (appCoordinator as any).handleTemplateSelection?.(template);
          onClose();
          logger.info(`Template chargé: ${template.name}`);
        } catch (error) {
          logger.error('Erreur lors du chargement du template:', error);
          // Fallback: ouvrir la galerie si le chargement échoue
          setShowTemplateGallery(true);
          onClose();
        }
      } else {
        // Fallback: ouvrir la galerie si AppCoordinator n'est pas disponible
        setShowTemplateGallery(true);
        onClose();
      }
    },
    [appCoordinator, setShowTemplateGallery, onClose]
  );

  return (
    <>
      <div className="drawer-header">
        <h3 className="drawer-title">Templates</h3>
        <button className="drawer-close-btn" onClick={onClose} aria-label="Fermer">
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      </div>
      {/* P3 redesign : search + filter pills */}
      <div className="sb-drawer-search">
        <div className="sb-drawer-search-input">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Chercher un template…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
        </div>
      </div>
      <div className="sb-drawer-filters" role="tablist" aria-label="Filtrer par catégorie">
        {CATEGORY_FILTERS.map(f => (
          <button
            key={f.id}
            role="tab"
            aria-selected={activeFilter === f.id}
            className={`sb-drawer-filter-pill ${activeFilter === f.id ? 'is-active' : ''}`}
            onClick={() => setActiveFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="drawer-content">
        {loading ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 'var(--spacing-xl)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="animate-spin"
            >
              <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="16" />
            </svg>
            <span style={{ marginLeft: 'var(--spacing-sm)' }}>Chargement des templates...</span>
          </div>
        ) : templates.length > 0 ? (
          <div className="drawer-grid">
            {/* P3 redesign : filtrer côté client (sur le set chargé) */}
            {templates
              .filter(t => activeFilter === 'all' || t.category === activeFilter)
              .filter(
                t =>
                  !searchQuery ||
                  t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  t.category?.toLowerCase().includes(searchQuery.toLowerCase())
              )
              .map(template => (
                <div
                  key={template.id}
                  className="drawer-item"
                  onClick={() => handleTemplateClick(template)}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className="drawer-item-preview"
                    style={{
                      background: template.thumbnail
                        ? `url(${template.thumbnail}) center/cover`
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: template.thumbnail ? 'transparent' : 'white',
                      fontSize: '14px',
                      fontWeight: '600',
                    }}
                  >
                    {!template.thumbnail && <span>{template.name || template.category}</span>}
                  </div>
                  <p className="drawer-item-title">{template.name || template.category}</p>
                </div>
              ))}
            <div className="drawer-item" onClick={handleOpenGallery} style={{ cursor: 'pointer' }}>
              <div
                className="drawer-item-preview"
                style={{
                  background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed var(--color-border)',
                }}
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="7" height="7" />
                  <rect x="14" y="3" width="7" height="7" />
                  <rect x="14" y="14" width="7" height="7" />
                  <rect x="3" y="14" width="7" height="7" />
                </svg>
              </div>
              <p className="drawer-item-title">Voir tous</p>
            </div>
            {hasMore && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  marginTop: 'var(--spacing-md)',
                  padding: 'var(--spacing-sm)',
                }}
              >
                <button
                  className="btn-secondary"
                  onClick={loadMoreTemplates}
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: 'var(--spacing-sm)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 'var(--spacing-xs)',
                  }}
                >
                  {loading ? (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        className="animate-spin"
                      >
                        <circle cx="12" cy="12" r="10" strokeDasharray="32" strokeDashoffset="16" />
                      </svg>
                      Chargement...
                    </>
                  ) : (
                    <>
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                      Charger plus de templates
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div
            style={{
              textAlign: 'center',
              padding: 'var(--spacing-xl)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <p>Aucun template disponible</p>
            <button
              className="btn-secondary"
              onClick={handleOpenGallery}
              style={{ marginTop: 'var(--spacing-md)' }}
            >
              Ouvrir la galerie
            </button>
          </div>
        )}
      </div>
    </>
  );
};
